import { oauthTokenSelector } from 'selectors/app/base'
import config from 'config'
import { batchActions } from 'util/redux'
import { BEGIN, COMMIT, REVERT } from 'redux-optimist'
import { v4 as uuidV4 } from 'uuid'
import { redirect } from 'redux-first-router'
import graphql from 'api/graphql'
import {
  SETTINGS_WIDGET_FIRST_INSTALL_PAGE,
  SETTINGS_WIDGETS_PAGE,
} from 'subapps/settings/types'

import {
  createFetchWidgetsRequestAction,
  createFetchWidgetsSuccessAction,
  createFetchWidgetsFailAction,
} from './createFetchWidgets'

import {
  widgetsQuery,
  settingsQuery,
  publishSettingsMutation,
  revertWidgetSettingsMutation,
  updateWidgetSettingsMutation,
  createWidgetMutation,
  updateWidgetMutation,
  deleteWidgetMutation,
} from '../api'

import {
  BOOTSTRAP_WIDGETS,
  FETCH_WIDGET_SETTINGS_REQUEST,
  FETCH_WIDGET_SETTINGS_SUCCESS,
  FETCH_WIDGET_SETTINGS_FAILURE,
  FETCH_WIDGET_SNIPPET_REQUEST,
  FETCH_WIDGET_SNIPPET_SUCCESS,
  FETCH_WIDGET_SNIPPET_FAILURE,
  UPDATE_WIDGET_SETTINGS_REQUEST,
  UPDATE_WIDGET_SETTINGS_SUCCESS,
  UPDATE_WIDGET_SETTINGS_FAILURE,
  PUBLISH_WIDGET_SETTINGS_REQUEST,
  PUBLISH_WIDGET_SETTINGS_SUCCESS,
  PUBLISH_WIDGET_SETTINGS_FAILURE,
  REVERT_WIDGET_SETTINGS_REQUEST,
  REVERT_WIDGET_SETTINGS_SUCCESS,
  REVERT_WIDGET_SETTINGS_FAILURE,
  CREATE_WIDGET_REQUEST,
  CREATE_WIDGET_SUCCESS,
  CREATE_WIDGET_FAILURE,
  UPDATE_WIDGET_REQUEST,
  UPDATE_WIDGET_SUCCESS,
  UPDATE_WIDGET_FAILURE,
  DELETE_WIDGET_REQUEST,
  DELETE_WIDGET_SUCCESS,
  DELETE_WIDGET_FAILURE,
  UPDATE_WIDGET_PREVIEW_MESSAGE,
  UPDATE_WIDGET_DEMO_SETTINGS,
} from '../types'

const normalizeWidgetErrors = e => {
  const errorMessage = {}

  if (e && e.errors && e.errors[0] && e.errors[0].originalError) {
    const error = e.errors[0].originalError

    if (error.name) {
      errorMessage.name = error.name[0]
    }
  }
  return errorMessage
}

export const doFetchSettings = widgetId => {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({ type: FETCH_WIDGET_SETTINGS_REQUEST })

    const variables = {
      widgetId,
    }

    return graphql(token, settingsQuery(), variables)
      .then(res => {
        const data = res.json.data
        dispatch({
          type: FETCH_WIDGET_SETTINGS_SUCCESS,
          data: data.widgetSettings,
          widgetId,
        })
      })
      .catch(err => {
        dispatch({
          type: FETCH_WIDGET_SETTINGS_FAILURE,
          data: err,
        })
      })
  }
}

export const doFetchSnippet = () => {
  return dispatch => {
    dispatch({
      type: FETCH_WIDGET_SNIPPET_REQUEST,
    })

    // use template variables to replace later for the widget
    fetch(`${config.widgetUrl}/api/snippet`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/javascript',
      },
    })
      .then(response => response.text())
      .then(body => {
        dispatch({
          type: FETCH_WIDGET_SNIPPET_SUCCESS,
          data: body,
        })
      })
      .catch(err => {
        dispatch({
          type: FETCH_WIDGET_SNIPPET_FAILURE,
          data: { err },
        })
      })
  }
}

export const doUpdateSettings = (_prefs, id) => {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const tid = uuidV4()
    const widgetId = id || state.location.payload.id

    const prefs = _prefs || {}

    dispatch({
      type: UPDATE_WIDGET_SETTINGS_REQUEST,
      // Optimistically update settings, so that the preview is immediate
      data: prefs,
      optimist: { type: BEGIN, id: tid },
      widgetId,
    })

    const variables = {
      widgetId,
      input: Object.keys(prefs).reduce((acc, k) => {
        // eslint-disable-next-line no-unused-expressions
        typeof prefs[k] === 'object'
          ? (acc[k] = JSON.stringify(prefs[k]))
          : (acc[k] = prefs[k])
        return acc
      }, {}),
    }

    return graphql(token, updateWidgetSettingsMutation(), variables)
      .then(res => {
        const data = res.json.data

        dispatch({
          type: UPDATE_WIDGET_SETTINGS_SUCCESS,
          data: data.updateWidgetSettings,
          optimist: { type: COMMIT, id: tid },
          widgetId,
        })
      })
      .catch(err => {
        dispatch({
          type: UPDATE_WIDGET_SETTINGS_FAILURE,
          data: { err },
          optimist: { type: REVERT, id: tid },
        })
      })
  }
}

export default function doFetchWidgets() {
  return (dispatch, getState) => {
    const state = getState()

    dispatch(createFetchWidgetsRequestAction())

    return graphql(oauthTokenSelector(state), widgetsQuery())
      .then(res => {
        const data = res.json.data
        const widgets = data.widgets.data

        // load data for each widget
        widgets.forEach(({ id }) => dispatch(doFetchSettings(id)))

        dispatch(createFetchWidgetsSuccessAction(res))
      })
      .catch(err => {
        dispatch(createFetchWidgetsFailAction(err))
        throw err
      })
  }
}

export function doPublishSettings() {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const { id } = state.location.payload

    dispatch({
      type: PUBLISH_WIDGET_SETTINGS_REQUEST,
    })

    const variables = {
      widgetId: id,
    }

    return graphql(token, publishSettingsMutation(), variables)
      .then(res => {
        const data = res.json.data

        dispatch({
          type: PUBLISH_WIDGET_SETTINGS_SUCCESS,
          data: data.publishWidgetSettings,
          widgetId: id,
        })
      })
      .catch(err => {
        dispatch({
          type: PUBLISH_WIDGET_SETTINGS_FAILURE,
          data: { err },
        })
      })
  }
}

export function doRevertSettings() {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const { id } = state.location.payload

    dispatch({
      type: REVERT_WIDGET_SETTINGS_REQUEST,
    })

    const variables = {
      widgetId: id,
    }

    return graphql(token, revertWidgetSettingsMutation(), variables)
      .then(res => {
        const data = res.json.data

        dispatch({
          type: REVERT_WIDGET_SETTINGS_SUCCESS,
          data: data.revertWidgetSettings,
          widgetId: id,
        })
      })
      .catch(err => {
        dispatch({
          type: REVERT_WIDGET_SETTINGS_FAILURE,
          data: { err },
        })
      })
  }
}

export function doCreateWidget(name, settings) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({
      type: CREATE_WIDGET_REQUEST,
    })

    const variables = {
      input: {
        name,
      },
    }

    return graphql(token, createWidgetMutation(), variables)
      .then(res => {
        const data = res.json.data
        const widget = data.createWidget
        const { id } = widget

        dispatch(
          batchActions(
            {
              type: CREATE_WIDGET_SUCCESS,
              data: widget,
            },
            doUpdateSettings(settings, id)
          )
        )
        dispatch(
          redirect({
            type: SETTINGS_WIDGET_FIRST_INSTALL_PAGE,
            payload: { id },
          })
        )
      })
      .catch(e => {
        dispatch({
          type: CREATE_WIDGET_FAILURE,
          data: { err: normalizeWidgetErrors(e) },
        })
      })
  }
}

export function doUpdateWidget(widgetId, widget) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({
      type: UPDATE_WIDGET_REQUEST,
    })

    const variables = {
      id: widgetId,
      input: widget,
    }

    return graphql(token, updateWidgetMutation(), variables)
      .then(res => {
        const data = res.json.data
        const updatedWidget = data.updateWidget

        dispatch({
          type: UPDATE_WIDGET_SUCCESS,
          data: updatedWidget,
        })
      })
      .catch(err => {
        dispatch({
          type: UPDATE_WIDGET_FAILURE,
          data: { err: normalizeWidgetErrors(err) },
        })
      })
  }
}

export function doDeleteWidget(widgetId) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({
      type: DELETE_WIDGET_REQUEST,
    })

    const variables = {
      id: widgetId,
    }

    return graphql(token, deleteWidgetMutation(), variables)
      .then(() => {
        dispatch({
          type: DELETE_WIDGET_SUCCESS,
          widgetId,
        })
        dispatch(
          redirect({
            type: SETTINGS_WIDGETS_PAGE,
          })
        )
      })
      .catch(err => {
        dispatch({
          type: DELETE_WIDGET_FAILURE,
          data: { err },
        })
      })
  }
}

export function doBootstrapWidgets() {
  // eslint-disable-next-line func-names
  return function(dispatch, getState) {
    const state = getState()
    const isBootstrapped = !!state.widgets.isBootstrapped
    if (isBootstrapped) return
    dispatch(
      batchActions(
        { type: BOOTSTRAP_WIDGETS },
        doFetchWidgets(),
        doFetchSnippet()
      )
    )
  }
}

export function doHidePreviewMessage() {
  return {
    type: UPDATE_WIDGET_PREVIEW_MESSAGE,
    data: {
      hide: true,
    },
  }
}

export function doUpdateDemoSettings(settings) {
  return {
    type: UPDATE_WIDGET_DEMO_SETTINGS,
    data: settings,
  }
}
